<template>
    <r-form
            ref="form"
            :column-size="1"
            :crud="true"
            :disabled.sync="disabled"
            :validators="validators"
            :showHeader="false"
            @submit="save"
            title="property details"
            :columnOptions="{ md: 6 }"
            v-model="form"
            hide-loading
            read-only-disabled-mode
            @update:loading="loading = $event"
    >
        <template slot="buttons"
          v-if="!$can($perm.PROPERTIES_EDIT_PROPERTIES, $perm.PERMISSION_TYPE_PROPERTIES) && !$can($perm.PROPERTIES_EDIT_PROPERTY_ACCESS, $perm.PERMISSION_TYPE_PROPERTIES)"
        >
            <div></div>
        </template>
    </r-form>
</template>

<script>
import RForm from '@/components/Form/RiskhubForm';
import Property from '@/services/Property/Property';
import { forEach } from 'lodash';
import { form as PropertyForm, validators as PropertyValidators } from '@/services/Property/PropertyForm';
import ErrorMixin from '@/components/Notifications/ErrorMixin';

export default {
  name: 'Property-Item',
  components: {
    RForm,
  },
  mixins: [ErrorMixin],
  props: {
    property: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      disabled: true,
      loading: false,
      form: null,
      validators: PropertyValidators(),
    };
  },
  computed: {
    permissionsKey() {
      return [
        this.$can(this.$perm.PROPERTIES_EDIT_PROPERTIES, this.$perm.PERMISSION_TYPE_PROPERTIES),
        this.$can(this.$perm.PROPERTIES_EDIT_PROPERTY_ACCESS, this.$perm.PERMISSION_TYPE_PROPERTIES),
        this.$can(this.$perm.PROPERTIES_EDIT_PROPERTY_TENURE, this.$perm.PERMISSION_TYPE_PROPERTIES),
      ].join('|');
    },
  },
  watch: {
    permissionsKey() {
      this.validators = PropertyValidators();
      this.setPropertyForm();
    },
    'form.propertyStatusId.value': {
      handler(newPropertyStatusIdValue) {
        /**
         *  Do not change any values while loading.
         * This is useful for when we save the property details and go back
         * to the "disabled" property form
         */
        if (this.loading) {
          return;
        }

        if (!newPropertyStatusIdValue) {
          return;
        }

        const { propertyStatusId } = newPropertyStatusIdValue;

        if (!propertyStatusId) {
          return;
        }

        if (propertyStatusId !== this.property.propertyStatusId) {
          this.form.statusComment.value = '';
          this.$refs.form.$v.fields.statusComment.$reset();
        }
      },
    },
    loading(newValue) {
      this.$emit('update:loading', newValue);
    },
  },
  methods: {
    async save(form) {
      this.loading = true;

      const { propertyId } = this.property;
      const params = {};

      forEach(form, (object, field) => {
        const { value, disabled } = object;

        if (value === null) {
          return true;
        }

        if (disabled) {
          return true;
        }

        params[field] = object.value;
      });

      try {
        await Property.update(propertyId, params);
        this.$notify({
          title: 'Property updated',
          text: 'Property has been successfully updated',
          type: 'success',
        });

        this.handlePostSaveUIUpdate(params);

        this.disabled = true;

        this.$emit('save');
      } catch (e) {
        this.showValidationError(e);
      } finally {
        this.loading = false;
      }
    },
    handlePostSaveUIUpdate(params) {
      const fieldsToUpdate = [
        'tradeButtonAccess',
        'fireKeyRequired',
        'fobKeyRequired',
        'keyCodeRequired'
      ];

      fieldsToUpdate.forEach(fieldKey => {
        let selectedOption = this.findOptionByValue(this.form[fieldKey].options, params[fieldKey]);
        if (!selectedOption) {
          selectedOption = { value: null, label: 'Select option' };
        }
        this.$set(this.form[fieldKey], 'value', selectedOption);

        // Clear related fields based on conditions
        if (params[fieldKey] !== 1) {
          const relatedFields = this.getRelatedFields(fieldKey);
          relatedFields.forEach(relatedFieldKey => {
            const relatedField = this.form[relatedFieldKey];
            // Check if the related field is a dropdown
            if (relatedField.options) {
              const defaultOption = { value: null, label: 'Select option' };
              this.$set(relatedField, 'value', defaultOption);
            } else {
              this.$set(relatedField, 'value', null);
            }
          });
        }
      });
    },
    findOptionByValue(options, value) {
        return options.find(option => option.value === value);
    },
    getRelatedFields(fieldKey) {
      // Mapping of fields to their related fields
      const relatedFieldsMapping = {
        'tradeButtonAccess': ['tradeButtonInfo'],
        'fireKeyRequired': ['fireKeyTypeId'],
        'fobKeyRequired': ['keyTypeId', 'fobKeyInfo'],
        'keyCodeRequired': ['keyCodeInfo'],
      };

      // Return the related fields for the given fieldKey, 
      //or an empty array if none are found
      return relatedFieldsMapping[fieldKey] || [];
    },
    setPropertyForm() {
      let form = PropertyForm();
      delete form.masterReference;
      delete this.validators.masterReference;
      const property = this.property;

      form = {
        uprn: {
          type: 'text',
          label: 'UPRN',
          value: '',
          disabled: true,
          columnOptions: {
            md: 12,
          },
        },
        ...form,
      };

      forEach(property, (value, key) => {
        if (key in form) {
          this.$set(form[key], 'value', value);
        }
      });

      // Handle static dropdown fields
      const staticFieldsToSet = [
        'blockNumberParity',
        'tradeButtonAccess', 
        'fireKeyRequired', 
        'fobKeyRequired', 
        'keyCodeRequired'
      ];
      staticFieldsToSet.forEach(field => {
        const option = form[field].options.find(option => option.value === this.property[field]);
        if (option) {
          this.$set(form[field], 'value', option);
        }
      });

      // Handle dynamic dropdown fields
      const dynamicDropdowns = [
        { 
          propertyName: 'paKeyTypes', 
          formField: 'keyTypeId', 
          valueType: 'keyTypeId', 
          labelType: 'keyType' 
        },
        { 
          propertyName: 'paFireKeyTypes', 
          formField: 'fireKeyTypeId', 
          valueType: 'fireKeyTypeId', 
          labelType: 'fireKeyType' 
        }
      ];

      dynamicDropdowns.forEach(dropdown => {
        // Dynamically set options
        form[dropdown.formField].options = this.property[dropdown.propertyName].map(item => ({
          value: item[dropdown.valueType],
          label: item[dropdown.labelType]
        }));

        // Set the selected option
        const selectedOption = form[dropdown.formField].options.find(
          option => option.value === this.property[dropdown.formField]
        );
        if (selectedOption) {
          this.$set(form[dropdown.formField], 'value', selectedOption);
        }
      });
      if (!this.$can(this.$perm.PROPERTIES_EDIT_PROPERTIES, this.$perm.PERMISSION_TYPE_PROPERTIES)) {
        Object.keys(form).forEach(field => {
          const canEditField = form[field].permissionGroup === 'access'
            ? this.$can(this.$perm.PROPERTIES_EDIT_PROPERTY_ACCESS, this.$perm.PERMISSION_TYPE_PROPERTIES)
            : form[field].permissionGroup === 'tenure'
              ? this.$can(this.$perm.PROPERTIES_EDIT_PROPERTY_TENURE, this.$perm.PERMISSION_TYPE_PROPERTIES)
              : false;
          if (!canEditField) {
            this.$set(form[field], 'disabled', true);
            this.$delete(this.validators, field);
          }
        });
      }

      this.form = form;
    },
  },
  created() {
    this.setPropertyForm();
  },
};
</script>
